import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  onSearch?: (query: string) => void;
}

export default function Navbar({ onSearch }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch && searchQuery.trim()) {
      onSearch(searchQuery.trim());
      setSearchQuery('');
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-950/95 backdrop-blur-md shadow-lg shadow-black/20'
          : 'bg-gradient-to-b from-black/80 to-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <Link
              to="/"
              className="flex items-center gap-2 group"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500">
                <i className="ri-movie-2-line text-lg text-background-950"></i>
              </span>
              <span className="text-xl font-heading font-bold text-foreground-50 whitespace-nowrap">
                被窝影院
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              <NavLink to="/" label="首页" />
              <NavLink to="/category/movie" label="电影" />
              <NavLink to="/category/tv" label="电视剧" />
              <NavLink to="/category/anime" label="动漫" />
              <NavLink to="/category/variety" label="综艺" />
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <form onSubmit={handleSearchSubmit} className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索影片..."
                className="w-48 lg:w-56 h-9 pl-9 pr-3 text-sm rounded-full bg-background-800/60 border border-background-700/50 text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 focus:bg-background-800 transition-all"
              />
              <button
                type="submit"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-search-line text-sm text-foreground-400"></i>
              </button>
            </form>
            <Link
              to="/about"
              className="text-sm text-foreground-300 hover:text-foreground-100 transition-colors whitespace-nowrap"
            >
              关于我们
            </Link>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="菜单"
          >
            <i
              className={`text-xl text-foreground-100 ${
                isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
              }`}
            ></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden pb-4 border-t border-background-800/50">
            <div className="flex flex-col gap-1 pt-3">
              <MobileNavLink to="/" label="首页" />
              <MobileNavLink to="/category/movie" label="电影" />
              <MobileNavLink to="/category/tv" label="电视剧" />
              <MobileNavLink to="/category/anime" label="动漫" />
              <MobileNavLink to="/category/variety" label="综艺" />
              <MobileNavLink to="/about" label="关于我们" />
            </div>
            <form onSubmit={handleSearchSubmit} className="mt-3 px-1">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索影片..."
                  className="w-full h-10 pl-10 pr-4 text-sm rounded-full bg-background-800/60 border border-background-700/50 text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50"
                />
                <button
                  type="submit"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-search-line text-sm text-foreground-400"></i>
                </button>
              </div>
            </form>
          </div>
        )}
      </nav>
    </header>
  );
}

function NavLink({ to, label }: { to: string; label: string }) {
  const location = useLocation();
  const isActive = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));

  return (
    <Link
      to={to}
      className={`px-3 py-2 text-sm rounded-lg transition-all whitespace-nowrap ${
        isActive
          ? 'text-primary-400 bg-primary-500/10'
          : 'text-foreground-300 hover:text-foreground-100 hover:bg-background-800/40'
      }`}
    >
      {label}
    </Link>
  );
}

function MobileNavLink({ to, label }: { to: string; label: string }) {
  const location = useLocation();
  const isActive = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));

  return (
    <Link
      to={to}
      className={`px-4 py-3 text-sm rounded-lg transition-all ${
        isActive
          ? 'text-primary-400 bg-primary-500/10'
          : 'text-foreground-300 hover:text-foreground-100'
      }`}
    >
      {label}
    </Link>
  );
}